import {
  type JSXElementConstructor,
  type ReactElement,
  useMemo,
  useState,
} from 'react';

import { Box, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  type GridActionsCellItemProps,
  type GridColDef,
  type GridEventListener,
  type GridRowParams,
  type GridRowsProp,
  gridClasses,
} from '@mui/x-data-grid-premium';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type GetListingOffersQuery,
  type GetOffersQuery,
  Offers_Status_Enum,
} from '@graphql/__generated__/graphql';
import { ListingCell } from '@modules/_shared/components/data-grid/ListingCell';
import { ListingPriceCell } from '@modules/_shared/components/data-grid/ListingPriceCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { ChangeStatusModal } from '@modules/_shared/components/offers/ChangeStatusModal';
import { Tag } from '@modules/_shared/components/Tag';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import {
  type Me,
  useAppData,
} from '@modules/_shared/providers/AppDataProvider';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { type Currency, getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

type OfferRow =
  | GetOffersQuery['offers'][number]
  | GetListingOffersQuery['offers'][number];

const isGlobalOffersRow = (
  row: OfferRow,
): row is GetOffersQuery['offers'][number] => {
  return (
    row.lot?.property != null && 'single_property_images' in row.lot.property
  );
};

type OffersTableProps = {
  rows: GridRowsProp<OfferRow>;
  fromListing?: boolean;
  rowCount?: number;
  isLoading?: boolean;
  onRowClick?: GridEventListener<'rowClick'>;
};

export const getOfferStatusLabel = (offer: OfferRow, t: Translate) => {
  switch (offer.status) {
    case Offers_Status_Enum.Pending:
      return t('pending');
    case Offers_Status_Enum.Accepted:
      return t('accepted');
    case Offers_Status_Enum.Refused:
      return `${t('refused')}${
        offer.offer_refused_type?.label
          ? ` - ${offer.offer_refused_type.label}`
          : ''
      }`;
  }
};

const getOfferRowActions = ({
  params,
  disableActions = false,
  onStatusChange,
  t,
  currentUser,
}: {
  params: GridRowParams<OfferRow>;
  disableActions?: boolean;
  onStatusChange: (
    offer: OfferRow,
    status: Offers_Status_Enum.Accepted | Offers_Status_Enum.Refused,
  ) => void;
  t: Translate;
  currentUser: Me;
}): ReactElement<
  GridActionsCellItemProps,
  string | JSXElementConstructor<any>
>[] => {
  if (
    currentUser?.id !== params.row.created_by &&
    currentUser?.is_admin !== true
  ) {
    return [];
  }

  return params.row.status === 'pending'
    ? [
        <GridActionsCellItem
          showInMenu={true}
          label={t('Refused')}
          onClick={() => onStatusChange(params.row, Offers_Status_Enum.Refused)}
          key="update-offer-status-refused-action"
          disabled={disableActions}
        />,
        <GridActionsCellItem
          showInMenu={true}
          label={t('Accepted')}
          onClick={() =>
            onStatusChange(params.row, Offers_Status_Enum.Accepted)
          }
          key="update-offer-status-accepted-action"
          disabled={disableActions}
        />,
      ]
    : [];
};

export const OffersTable: React.FC<OffersTableProps> = ({
  rows,
  fromListing = false,
  rowCount,
  isLoading,
  onRowClick,
}) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const [searchParams] = useSearchParams();
  const [statusChangeSelection, setStatusChangeSelection] = useState<{
    offer: OfferRow;
    status: Offers_Status_Enum.Accepted | Offers_Status_Enum.Refused;
  } | null>(null);
  const navigate = useNavigate();

  const columns: GridColDef<OfferRow>[] = useMemo(
    () => [
      ...(fromListing
        ? []
        : ([
            {
              field: 'lot.title',
              headerName: t('Listing'),
              width: 250,
              display: 'flex',
              renderCell: ({ row }) =>
                isGlobalOffersRow(row) ? (
                  <ListingCell listing={row.lot} />
                ) : null,
            },
            {
              field: 'lot.sale_price',
              headerName: t('Listing price'),
              width: 100,
              align: 'right',
              display: 'flex',
              renderHeader: () => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: '20px',
                      fontWeight: 500,
                    }}
                  >
                    {t('Price')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      lineHeight: '20px',
                    }}
                  >
                    {t('Appraisal')}
                  </Typography>
                </Box>
              ),
              renderCell: ({ row }) =>
                isGlobalOffersRow(row) ? (
                  <ListingPriceCell row={row.lot} />
                ) : null,
            },
          ] as GridColDef<OfferRow>[])),
      {
        field: 'amount',
        headerName: t('Offer amount'),
        display: 'flex',
        width: 200,
        renderCell: ({ row }) => {
          const amount = row.amount;
          const currency = (row.lot?.currency ??
            getCurrencyByCountryCode(
              row.lot?.property?.country_code ?? 'CH',
            )) as Currency;

          return formatPrice(amount, locale, currency);
        },
      },
      {
        field: 'status',
        headerName: t('Offer status'),
        sortable: false,
        display: 'flex',
        width: 250,
        renderCell: ({ row }) => {
          return (
            <Tag
              status={
                row.status === Offers_Status_Enum.Pending
                  ? 'info'
                  : row.status === Offers_Status_Enum.Accepted
                  ? 'success'
                  : 'error'
              }
            >
              {getOfferStatusLabel(row, t)}
            </Tag>
          );
        },
      },
      {
        field: 'buyer',
        headerName: t('Buyer'),
        display: 'flex',
        width: 280,
        sortable: false,
        renderCell: ({ row }) => <UserInfo user={row.buyer} />,
      },
      {
        field: 'valid_until',
        headerName: t('Valid until'),
        width: 150,
        renderCell: ({ row }) =>
          row.valid_until != null
            ? toHumanReadableDate(locale, row.valid_until, {
                month: 'numeric',
              })
            : '-',
      },
      {
        field: 'made_on',
        headerName: t('Offer made'),
        width: 150,
        renderCell: ({ row }) => (
          <TimeAgo dateString={row.made_on} ignoreTime addSuffix />
        ),
      },
      ...(fromListing
        ? []
        : ([
            {
              field: 'lot.broker',
              headerName: t('Agent'),
              display: 'flex',
              width: 250,
              sortable: false,
              renderCell: ({ row }) =>
                row.lot?.broker != null ? (
                  <UserInfo user={row.lot.broker} />
                ) : null,
            },
          ] as GridColDef<OfferRow>[])),
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        flex: 1,
        getActions: params =>
          getOfferRowActions({
            params,
            t,
            currentUser: me,
            disableActions: false,
            onStatusChange: (row, status) => {
              setStatusChangeSelection({ offer: row, status });
            },
          }),
      },
    ],
    [me, locale, t, fromListing],
  );

  return (
    <>
      <RaDataGrid
        sx={{
          ...(fromListing ? { borderColor: 'transparent' } : {}),
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
            {
              outline: 'none',
            },
        }}
        loading={isLoading}
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        rowReordering={false}
        getRowId={row => row.id}
        rowHeight={60}
        disableMultipleRowSelection
        disableRowSelectionOnClick
        disableColumnReorder
        onRowClick={onRowClick}
        paginationMode={fromListing ? 'client' : 'server'}
        sortingMode={fromListing ? 'client' : 'server'}
        hideFooter={fromListing}
        autoHeight={fromListing}
      />
      <ChangeStatusModal
        offer={statusChangeSelection?.offer || null}
        open={statusChangeSelection != null}
        onClose={updateData => {
          setStatusChangeSelection(null);

          if (
            updateData?.status === Offers_Status_Enum.Accepted &&
            updateData.lot != null
          ) {
            const newSearchParams = fromListing
              ? searchParams
              : new URLSearchParams();
            newSearchParams.set('selected-listing', updateData.lot.id);
            newSearchParams.set('from-offer', updateData.id);

            navigate({
              pathname: `/listings/${updateData.lot.id}/transaction/new/listing`,
              search: newSearchParams.toString(),
            });
          }
        }}
        targetStatus={statusChangeSelection?.status}
      />
    </>
  );
};
